import {
  BadgedLink,
  Button,
  Checkbox,
  Flex,
  Heading1,
  Icon,
  IconArrowLeftSmallOutline,
  IconCheckmarkSmallFilled,
  IconLockSmallFilled,
  Input,
  Link,
  TextSmall,
} from '@teamleader/ahoy';
import { translate, Translation } from '@teamleader/i18n';
import React, { useCallback } from 'react';
import { Field, FormRenderProps } from 'react-final-form';
import { getFormFieldErrorFromMeta } from '../../../Infrastructure/Forms/utils';
import styles from './styles.css';
import { useMultiFactorAuthenticationContext } from './MultiFactorAuthenticationContext';
import { isSmsMultiFactorAuthentication } from '../../../Infrastructure/API/MultiFactorAuthentication/list';
import useMultiFactorAuthenticationRequest from './useMultiFactorAuthenticationRequest';
import useEffectOnInitialize from './useEffectOnInitialize';
import { useCountdownTimer } from './useCountdownTimer';
import { RETRY_MFA_REQUEST_COUNTDOWN_SECONDS } from './constants';

interface SmsProps {
  formRenderProps: FormRenderProps;
  countdownTimer: ReturnType<typeof useCountdownTimer>;
  onGoBackToLoginClick: () => void;
  onChooseAnotherMethodClick: () => void;
  isRedirecting: boolean;
}

const Sms = ({
  formRenderProps,
  countdownTimer,
  onGoBackToLoginClick,
  onChooseAnotherMethodClick,
  isRedirecting,
}: SmsProps) => {
  const { authentications, authToken } = useMultiFactorAuthenticationContext();
  const { submitting, submitError } = formRenderProps;
  const { secondsRemaining, set: setTimer } = countdownTimer;
  const smsAuthentication = authentications.find(isSmsMultiFactorAuthentication);
  const { request } = useMultiFactorAuthenticationRequest({ authToken });

  const handleRequest = useCallback(() => {
    if (secondsRemaining > 0) {
      return;
    }

    if (smsAuthentication) {
      request(smsAuthentication?.id);
      setTimer(RETRY_MFA_REQUEST_COUNTDOWN_SECONDS);
    }
  }, [secondsRemaining, smsAuthentication, request, setTimer]);

  useEffectOnInitialize(() => {
    handleRequest();
  });

  return (
    <Flex direction="column" alignItems="stretch">
      <BadgedLink
        className={styles.back}
        onClick={onGoBackToLoginClick}
        inherit={false}
        icon={<IconArrowLeftSmallOutline />}
      >
        <TextSmall>{translate('mfa.goBackToLogin')}</TextSmall>
      </BadgedLink>

      <Flex direction="column" alignItems="flex-start" gap={2} marginBottom={5}>
        <Heading1>{translate('mfa.smsTitle')}</Heading1>

        <Flex alignItems="center" gap={2}>
          <Icon color="mint">
            <IconCheckmarkSmallFilled />
          </Icon>

          <TextSmall color="neutral" tint="darkest">
            <Translation
              id="mfa.verificationCodeSentTo"
              values={{
                recipient: smsAuthentication?.number,
                strong: (chunks: string[]): JSX.Element => <strong>{chunks}</strong>,
              }}
            />
          </TextSmall>
        </Flex>
      </Flex>

      <Field name="mfaCode">
        {({ input, meta }) => {
          return (
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              autoFocus
              placeholder={translate('code')}
              prefix={
                <Icon color="neutral" tint="darkest">
                  <IconLockSmallFilled />
                </Icon>
              }
              {...input}
              error={getFormFieldErrorFromMeta(meta)}
              size="large"
              marginBottom={2}
            />
          );
        }}
      </Field>

      {submitError && (
        <TextSmall color="ruby" tint="dark">
          {submitError}
        </TextSmall>
      )}

      <TextSmall color="neutral" tint="darkest" textAlign="right">
        {secondsRemaining > 0 ? (
          <Translation
            id="mfa.newRequestCountdown"
            values={{
              secondsRemaining,
              strong: (chunks: string[]): JSX.Element => <strong>{chunks}</strong>,
            }}
          />
        ) : (
          <Translation
            id="mfa.requestNewCode"
            values={{
              link: (chunks: string[]): JSX.Element => (
                <Link inherit={false} onClick={handleRequest}>
                  {chunks}
                </Link>
              ),
            }}
          />
        )}
      </TextSmall>

      <Field name="mfaId">{({ input }) => <input {...input} type="hidden" value={smsAuthentication?.id} />}</Field>

      <Flex direction="column" alignItems="stretch" gap={3} marginTop={6}>
        <Field name="trusted" initialValue={false}>
          {({ input }) => {
            return (
              <Checkbox
                label={translate('trustDevice', { amountOfDays: 7 })}
                onChange={input.onChange}
                checked={input.value}
                alignSelf="flex-start"
              />
            );
          }}
        </Field>

        <Button size="large" level="primary" fullWidth type="submit" processing={submitting || isRedirecting}>
          {translate('mfa.validateAndLogin')}
        </Button>

        <Button size="medium" level="link" fullWidth onClick={onChooseAnotherMethodClick} type="button">
          {translate('mfa.useADifferentMethod')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default Sms;
